import { useLayoutEffect, useState, useSyncExternalStore, type RefObject } from 'react';

/**
 * Altura minima do quadro. Em tela muito baixa (zoom alto em notebook) a pagina rola
 * um pouco, o que e melhor do que colunas em que nao cabe nem um cartao.
 */
export const ALTURA_MINIMA_DO_QUADRO = 384;

export interface LeituraDoQuadro {
  /** Largura interna da area das colunas. Zero antes da primeira medida. */
  largura: number;
  /** Ha colunas escondidas a esquerda. */
  antes: boolean;
  /** Ha colunas escondidas a direita. */
  depois: boolean;
  /** Primeira e ultima coluna inteiras na tela, contando de 1. */
  primeira: number;
  ultima: number;
}

const inicial: LeituraDoQuadro = {
  largura: 0,
  antes: false,
  depois: false,
  primeira: 0,
  ultima: 0,
};

function ler(area: HTMLElement): LeituraDoQuadro {
  const { clientWidth, scrollWidth, scrollLeft } = area;
  // Um pixel de folga: com zoom o navegador arredonda a rolagem em fracoes.
  const inicioVisivel = scrollLeft - 1;
  const fimVisivel = scrollLeft + clientWidth + 1;
  let primeira = 0;
  let ultima = 0;
  let maisVisivel = 0;
  let maiorParte = 0;
  Array.from(area.children).forEach((filho, indice) => {
    const coluna = filho as HTMLElement;
    const inicio = coluna.offsetLeft;
    const fim = inicio + coluna.offsetWidth;
    if (inicio >= inicioVisivel && fim <= fimVisivel) {
      if (primeira === 0) primeira = indice + 1;
      ultima = indice + 1;
    }
    const parte = Math.min(fim, fimVisivel) - Math.max(inicio, inicioVisivel);
    if (parte > maiorParte) {
      maiorParte = parte;
      maisVisivel = indice + 1;
    }
  });
  return {
    largura: clientWidth,
    antes: scrollLeft > 1,
    depois: scrollLeft + clientWidth < scrollWidth - 1,
    // A coluna larga do celular pode parar entre duas: nenhuma fica inteira na tela, e
    // o aviso fala da que mais aparece.
    primeira: primeira || maisVisivel,
    ultima: ultima || maisVisivel,
  };
}

function mesmaLeitura(a: LeituraDoQuadro, b: LeituraDoQuadro): boolean {
  return (
    a.largura === b.largura &&
    a.antes === b.antes &&
    a.depois === b.depois &&
    a.primeira === b.primeira &&
    a.ultima === b.ultima
  );
}

/**
 * Faz as colunas caberem na janela: a area vai do topo do quadro ate o fim da tela,
 * descontado o que a pagina mostra depois dele. A lista longa rola dentro da coluna e
 * a pagina nao estica.
 */
function ajustarAltura(area: HTMLElement) {
  const caixa = area.getBoundingClientRect();
  const pagina = area.closest('main');
  const conteudo = pagina?.firstElementChild;
  const depoisDoQuadro = conteudo
    ? conteudo.getBoundingClientRect().bottom - caixa.bottom
    : 0;
  const margem = pagina ? parseFloat(getComputedStyle(pagina).paddingBottom) : 0;
  const topo = caixa.top + window.scrollY;
  const altura = Math.floor(window.innerHeight - topo - depoisDoQuadro - margem);
  area.style.height = `${Math.max(altura, ALTURA_MINIMA_DO_QUADRO)}px`;
}

/**
 * A tela vista como uma fonte de dados externa ao React. A leitura so troca de objeto
 * quando algo mudou: e isso que evita renderizar a cada rolagem que nao muda nada.
 */
function criarLeitor(referencia: RefObject<HTMLElement | null>) {
  let ultima = inicial;
  let avisarDaMudanca: (() => void) | null = null;

  return {
    fotografar: (): LeituraDoQuadro => {
      const area = referencia.current;
      if (area) {
        const nova = ler(area);
        if (!mesmaLeitura(ultima, nova)) ultima = nova;
      }
      return ultima;
    },

    /** Ajusta a altura e confere de novo o que cabe. */
    atualizar: () => {
      const area = referencia.current;
      if (area) ajustarAltura(area);
      avisarDaMudanca?.();
    },

    assinar: (avisar: () => void) => {
      const area = referencia.current;
      if (!area) return () => undefined;

      avisarDaMudanca = avisar;
      let pedido = 0;
      // Uma leitura por quadro de video basta, e fora do retorno do observador o ajuste
      // de altura nao dispara outra rodada de observacao no mesmo quadro.
      const agendar = () => {
        window.cancelAnimationFrame(pedido);
        pedido = window.requestAnimationFrame(() => {
          ajustarAltura(area);
          avisar();
        });
      };

      area.addEventListener('scroll', agendar, { passive: true });
      window.addEventListener('resize', agendar);

      // Os ancestrais tambem: o que muda a posicao do quadro esta neles, como o painel
      // de filtros que abre acima das colunas.
      const observador =
        typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(agendar);
      if (observador) {
        observador.observe(area);
        for (
          let ancestral = area.parentElement;
          ancestral && ancestral !== document.body;
          ancestral = ancestral.parentElement
        ) {
          observador.observe(ancestral);
        }
      }

      return () => {
        avisarDaMudanca = null;
        window.cancelAnimationFrame(pedido);
        area.removeEventListener('scroll', agendar);
        window.removeEventListener('resize', agendar);
        observador?.disconnect();
      };
    },
  };
}

/**
 * Le o quadro na tela: largura, altura disponivel e colunas fora de vista.
 *
 * A medida vem do proprio quadro, e nao da janela, para valer em qualquer combinacao
 * de zoom, tela e menu lateral.
 */
export function useLeituraDoQuadro(
  referencia: RefObject<HTMLElement | null>,
): LeituraDoQuadro {
  const [leitor] = useState(() => criarLeitor(referencia));
  const leitura = useSyncExternalStore(leitor.assinar, leitor.fotografar);

  // Depois de cada renderizacao e antes da pintura: o aviso de colunas muda a posicao
  // do quadro, e a largura e a quantidade de colunas mudam o que cabe.
  useLayoutEffect(() => {
    leitor.atualizar();
  });

  return leitura;
}
